import { useCallback, useEffect, useRef, useState } from 'react';

// Keeps the assessments list UI state — which company groups are expanded and
// the scroll position — in sessionStorage, so navigating into a report and back
// returns the user to exactly where they left off. The state is cleared when the
// list is entered fresh from the sidebar menu (see clearAssessmentsListState).
const KEY = 'assessments-list-state';

type PersistedState = {
  expanded: string[];
  scrollTop: number;
};

function read(): PersistedState {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return { expanded: [], scrollTop: 0 };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return { expanded: parsed.expanded ?? [], scrollTop: parsed.scrollTop ?? 0 };
  } catch {
    return { expanded: [], scrollTop: 0 };
  }
}

function write(state: PersistedState) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors (e.g. private mode or quota).
  }
}

export function clearAssessmentsListState() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Ignore storage errors.
  }
}

/**
 * Manages the expanded company groups and scroll position for the assessments
 * list, persisting them to sessionStorage across navigation.
 *
 * @param ready whether the list has rendered its rows, so the saved scroll
 *   position can be restored against the real content height.
 */
export function useAssessmentsListState(ready: boolean) {
  const initial = useRef<PersistedState | null>(null);
  if (initial.current === null) initial.current = read();

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initial.current!.expanded));
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  const scrollTopRef = useRef(initial.current.scrollTop);
  const rootRef = useRef<HTMLDivElement>(null);
  const hasRestored = useRef(false);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      write({ expanded: [...next], scrollTop: scrollTopRef.current });
      return next;
    });
  }, []);

  // Track the scroll position of the layout's scroll container, persisting it
  // when the list unmounts (e.g. when navigating into a report).
  useEffect(() => {
    const scroller = rootRef.current?.closest('main');
    if (!scroller) return;
    const handleScroll = () => {
      scrollTopRef.current = scroller.scrollTop;
    };
    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      write({ expanded: [...expandedRef.current], scrollTop: scrollTopRef.current });
    };
  }, []);

  // Restore the saved scroll position once the list has rendered its rows.
  useEffect(() => {
    if (hasRestored.current || !ready) return;
    const scroller = rootRef.current?.closest('main');
    if (scroller) scroller.scrollTop = scrollTopRef.current;
    hasRestored.current = true;
  }, [ready]);

  return { rootRef, expanded, toggleGroup };
}
